import type { CryptoSymbol } from '../../../types'
import { config } from '../../../config'
import { CRYPTO_ASSETS } from '../../../lib/constants'
import { cx, text } from '../../../lib/ui-primitives'
import { Button, Spinner } from '../../primitives'
import { CryptoAssetCard } from '../../compound'
import type { useKiosk } from '../../../hooks/useKiosk'

interface WelcomeScreenProps {
  kiosk: ReturnType<typeof useKiosk>
  onLogoPress: () => void
  onLogoRelease: () => void
  onLogoLeave: () => void
}

/**
 * WelcomeScreen - Landing screen with crypto selection
 *
 * Refactored to use:
 * - CryptoAssetCard (compound component)
 * - Button (primitive)
 * - Spinner (primitive)
 *
 * LOC reduced from 113 to ~60 (47% reduction)
 */
export function WelcomeScreen({
  kiosk,
  onLogoPress,
  onLogoRelease,
  onLogoLeave,
}: WelcomeScreenProps) {
  const { prices, pricesLoading, selectCrypto, setScreen } = kiosk

  return (
    <div className="flex flex-col h-full px-4 py-3 sm:px-6 sm:py-4">
      {/* Logo & Title - Admin access via long press */}
      <div
        className="text-center mb-4 sm:mb-5 cursor-pointer select-none shrink-0"
        onMouseDown={onLogoPress}
        onMouseUp={onLogoRelease}
        onMouseLeave={onLogoLeave}
        onTouchStart={onLogoPress}
        onTouchEnd={onLogoRelease}
        role="button"
        tabIndex={0}
        aria-label={`${config.businessName} logo - hold for admin access`}
      >
        <h1 className={cx(text.h1, 'text-xl sm:text-2xl')}>{config.businessName}</h1>
        <p className={cx('text-xs sm:text-sm', text.secondary)}>{config.businessTagline}</p>
      </div>

      {/* Crypto Selection - Scrollable if needed */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        <div className="w-full max-w-sm mx-auto">
          <p className={cx('text-center text-xs mb-3', text.tertiary)}>
            Select cryptocurrency to buy
          </p>

          {pricesLoading ? (
            <div className="text-center py-6" role="status" aria-label="Loading prices">
              <Spinner size="md" centered label="Loading rates" />
              <p className={cx('text-sm mt-2', text.tertiary)}>Loading rates...</p>
            </div>
          ) : (
            <div className="space-y-2">
              {Object.entries(CRYPTO_ASSETS).map(([key, asset]) => (
                <CryptoAssetCard
                  key={key}
                  asset={asset}
                  price={prices[key as CryptoSymbol]}
                  onClick={() => selectCrypto(key as CryptoSymbol)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Footer - Track Order */}
      <div className="pt-3 shrink-0">
        <Button
          variant="ghost"
          fullWidth
          size="sm"
          onClick={() => setScreen('lookup')}
          leftIcon="🔍"
        >
          Track Existing Order
        </Button>
      </div>
    </div>
  )
}
